import Konva from 'konva';
import { v4 as uuidv4 } from 'uuid';

import HaspObject from './HaspObject';

// color        color   0       The selected color in html format #rrggbb
// scale_width  uint16  25      The width of the color gradient ring (circular) or bar height (rectangular)
// pad_inner    int16   10      The padding between the circle and the inner preview circle (circular mode only)
// mode         string  "hue"   The aspect of the color being edited: "hue", "saturation", or "value"
// mode_fixed   bool    false   Disables long press behavior to cycle color modes
//
// Shape: when width > height → rectangular bar; otherwise → circular ring

export const CPICKER_MODE_OPTIONS = [
    { value: 'hue', description: 'hue' },
    { value: 'saturation', description: 'saturation' },
    { value: 'value', description: 'value' },
];

export default class HaspColorpicker extends HaspObject {
    hasp_mode_options = CPICKER_MODE_OPTIONS;

    get arcWidth() {
        return this.style('main').scale_width || 25;
    }

    get isRectMode() {
        return this.rect.width() > this.rect.height();
    }

    constructor(config) {
        super(config);
        this.rect.opacity(0);

        const w = config.width;
        const h = config.height || config.width;
        const size = Math.min(w, h);
        const halfW = w / 2;
        const halfH = h / 2;
        const halfSize = size / 2;
        const arcW = this.arcWidth;
        const mainStyle = this.style('main');
        const indicatorStyle = this.style('indicator');
        const padInner = mainStyle.pad_inner || 10;

        const indicatorBorderWidth = indicatorStyle.border_width ?? 0;

        this.center_ring = new Konva.Ring({
            id: uuidv4(),
            x: halfW,
            y: halfH,
            obj: "ring",
            outerRadius: halfSize - arcW - (indicatorBorderWidth / 4),
            innerRadius: Math.max(0, halfSize - arcW - padInner + (indicatorBorderWidth / 4)),
            fill: mainStyle.bg_color,
            stroke: indicatorStyle.border_color,
            strokeWidth: indicatorBorderWidth,
            listening: false,
        });

        this.center = new Konva.Circle({
            id: uuidv4(),
            x: halfW,
            y: halfH,
            obj: "circle",
            radius: Math.max(0, halfSize - arcW - padInner),
            fill: this.color ?? '#ff0000',
            strokeWidth: 0,
            listening: false,
        });

        this.on('transform', function () {
            if (this.scaleX() === 1) this.redrawObject();
        });
        this.on('transformend', () => {
            this.redrawObject();
            this.size(this.rect.size());
        });

        this.onStyle('main', 'bg_color', () => { this.center_ring.fill(this.style('main').bg_color); });
        this.onStyle('indicator', 'border_color', () => { this.center_ring.stroke(this.style('indicator').border_color); });
        this.onStyle('indicator', 'border_width', () => {
            const indicatorBorderWidth = this.style('indicator').border_width ?? 0;
            this.center_ring.strokeWidth(indicatorBorderWidth);
            this.redrawObject();
        });
        this.onStyle('knob', 'border_color', () => { if (this.knob) this.knob.stroke(this.style('knob').border_color); });
        this.onStyle('knob', 'border_width', () => { if (this.knob) this.knob.strokeWidth(this.style('knob').border_width ?? 0); });
        this.onStyle('main', 'pad_inner', () => { this.redrawObject(); });
        this.onStyle('main', 'scale_width', () => { this.redrawObject(); });
        // Bug fix: was this.on('color_value', ...) — the top-level property is
        // "color" (bare wire key), so this listener never fired from a real
        // color change before.
        this.on('color', () => {
            this.center.fill(this.color);
            if (this.knob) this.knob.fill(this.color);
            this.createGradientVisual();
        });
        // Bug fix: was this.on('hasp_mode', ...) — "mode" has no hasp:true
        // flag in the schema, so its wire key is the bare "mode", not
        // "hasp_mode". This listener never fired from a real mode change.
        this.on('mode', () => { this.createGradientVisual(); });

        this.createKnob();
        this.createGradientVisual();
        this.add(this.center_ring);
        this.add(this.center);
        this.draggable(true);

        // Apply rect-mode visibility on first render
        if (w > h) {
            this.center_ring.visible(false);
            this.center.visible(false);
        }
    }

    getGradientColors() {
        if (this.mode === 'saturation') return ['#fff', this.color];
        if (this.mode === 'value') return [this.color, '#000'];
        // hue — full rainbow cycle
        return ['#FF0', '#F00', '#F0F', '#0F0', '#0FF', '#FF0'];
    }

    createGradientVisual() {
        const w = this.rect.width();
        const h = this.rect.height();
        const colors = this.getGradientColors();

        if (this.ring_img !== undefined) this.ring_img.destroy();

        let ring_canvas, imgX, imgY;
        if (w > h) {
            ring_canvas = this.drawGradientBar(w, h, colors);
            imgX = 0;
            imgY = 0;
        } else {
            const size = Math.min(w, h);
            const halfSize = size / 2;
            ring_canvas = this.drawMultiRadiantCircle(halfSize - (this.arcWidth / 2), this.arcWidth, colors);
            // Center the ring canvas within the widget bounds
            imgX = (w - ring_canvas.width) / 2;
            imgY = (h - ring_canvas.height) / 2;
        }

        this.ring_img = new Konva.Image({
            x: imgX,
            y: imgY,
            image: ring_canvas,
            width: ring_canvas.width,
            height: ring_canvas.height,
            draggable: false,
        });
        this.add(this.ring_img);
        // Re-establish z-order: ring_img sits below center_ring/center/knob
        if (this.center_ring && this.center_ring.getParent()) this.center_ring.moveToTop();
        if (this.center && this.center.getParent()) this.center.moveToTop();
        if (this.knob) this.knob.moveToTop();
    }

    createKnob() {
        if (this.knob !== undefined) {
            this.knob.destroy();
            this.knob = undefined;
        }
        const w = this.rect.width();
        const h = this.rect.height();
        const arcW = this.arcWidth;

        let knobX, knobY, knobRadius;
        if (w > h) {
            // Rectangular: knob sits at the left end of the bar
            knobRadius = h / 2;
            knobX = knobRadius;
            knobY = h / 2;
        } else {
            // Circular: knob sits at the bottom of the ring
            const size = Math.min(w, h);
            knobRadius = arcW / 2;
            knobX = w / 2;
            knobY = (h - size) / 2 + size - arcW / 2;
        }

        const knobStyle = this.style('knob');
        this.knob = new Konva.Circle({
            x: knobX,
            y: knobY,
            radius: knobRadius,
            fill: this.color ?? '#ff0000',
            stroke: knobStyle.border_color,
            strokeWidth: knobStyle.border_width ?? 0,
        });

        this.add(this.knob);
    }

    setNewSize(size) {
        super.setNewSize(size);
        this.redrawObject();
    }

    redrawObject() {
        const w = this.rect.width();
        const h = this.rect.height();
        const size = Math.min(w, h);
        const halfW = w / 2;
        const halfH = h / 2;
        const halfSize = size / 2;
        const arcW = this.arcWidth;
        const padInner = this.style('main').pad_inner || 10;

        this.createKnob();
        this.createGradientVisual();

        if (w > h) {
            this.center_ring.visible(false);
            this.center.visible(false);
        } else {
            this.center_ring.visible(true);
            this.center.visible(true);
            this.center_ring.x(halfW);
            this.center_ring.y(halfH);
            const indicatorBorderWidth = this.style('indicator').border_width ?? 0;
            this.center_ring.outerRadius(halfSize - arcW - (indicatorBorderWidth / 4));
            this.center_ring.innerRadius(Math.max(0, halfSize - arcW - padInner + (indicatorBorderWidth / 4)));
            this.center.x(halfW);
            this.center.y(halfH);
            this.center.radius(Math.max(0, halfSize - arcW - padInner));
        }
    }

    // Draws a rounded-rectangle gradient bar for rectangular mode
    drawGradientBar(w, h, colors) {
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');

        const gradient = ctx.createLinearGradient(0, 0, w, 0);
        colors.forEach((color, i) => {
            gradient.addColorStop(i / (colors.length - 1), color);
        });

        const radius = h / 2;
        ctx.beginPath();
        ctx.moveTo(radius, 0);
        ctx.arcTo(w, 0, w, h, radius);
        ctx.arcTo(w, h, 0, h, radius);
        ctx.arcTo(0, h, 0, 0, radius);
        ctx.arcTo(0, 0, w, 0, radius);
        ctx.closePath();
        ctx.fillStyle = gradient;
        ctx.fill();

        return canvas;
    }

    // Draws the multi-segment radial gradient ring for circular mode
    drawMultiRadiantCircle(r, linewidth, radientColors) {
        const xc = r + linewidth / 2;
        const yc = r + linewidth / 2;
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = r * 2 + linewidth;
        canvas.height = r * 2 + linewidth;

        const partLength = (2 * Math.PI) / radientColors.length;
        let start = 0;

        for (let i = 0; i < radientColors.length; i++) {
            const startColor = radientColors[i];
            const endColor = radientColors[(i + 1) % radientColors.length];
            const xStart = xc + Math.cos(start) * r;
            const xEnd = xc + Math.cos(start + partLength) * r;
            const yStart = yc + Math.sin(start) * r;
            const yEnd = yc + Math.sin(start + partLength) * r;

            const gradient = ctx.createLinearGradient(xStart, yStart, xEnd, yEnd);
            gradient.addColorStop(0, startColor);
            gradient.addColorStop(1.0, endColor);

            ctx.beginPath();
            ctx.strokeStyle = gradient;
            ctx.arc(xc, yc, r, start, start + partLength);
            ctx.lineWidth = linewidth;
            ctx.stroke();
            ctx.closePath();
            start += partLength;
        }
        return canvas;
    }

    objectExport(_page, _objectData, _hassConfig) {
    }
}
